import { Link } from "react-router-dom";
import { Phone, ArrowRight, PhoneCall } from "lucide-react";
import { SERVICES, PHONE_DISPLAY, PHONE_TEL, IMAGES } from "../data/content";
import { ServiceCard } from "../components/home/ServicesPreview";
import { GarageNote } from "../components/GarageNote";
import { useSeo } from "../hooks/useSeo";

export default function Services() {
  useSeo({
    title: "Mobile Mechanic Services Las Vegas | Mason's Mobile Mechanic",
    description: "Full mobile auto repair menu for Las Vegas, Henderson, Enterprise and Spring Valley: diagnostics, brakes, batteries, starters, alternators, suspension, fleet service and more — we come to you.",
    canonical: "https://masonsmobile.com/services",
  });
  return (
    <main data-testid="services-page" className="pt-20 md:pt-24">
      <section className="relative py-24 sm:py-28 overflow-hidden border-b border-zinc-800/60">
        <div className="absolute inset-0">
          <img src={IMAGES.services} alt="Close-up of a car engine during mobile auto repair service in Las Vegas" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500">Full Service Menu</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none text-white mt-4 mb-6">
            {SERVICES.length} Ways We Keep<br />You Moving
          </h1>
          <p className="text-zinc-300 max-w-2xl leading-relaxed">
            From a check engine light to a complete brake job — every service below is performed
            at your location in Las Vegas, Henderson, Enterprise, Spring Valley and nearby areas,
            with professional tools, quality parts, and upfront pricing.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20 border-b border-zinc-800/60" data-testid="phone-quote-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-r from-blue-600/15 to-transparent border border-blue-500/30 rounded-xl p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="bg-blue-600/10 border border-blue-500/20 rounded-sm p-3 shrink-0">
                <PhoneCall className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-2">
                  Reliable Quotes Over the Phone
                </h2>
                <p className="text-zinc-400 leading-relaxed max-w-xl">
                  Tell us your vehicle and the problem — we can generally give you a reliable
                  quote right over the phone, before any work begins.
                </p>
              </div>
            </div>
            <a
              data-testid="phone-quote-call-button"
              href={PHONE_TEL}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-8 py-4 font-semibold transition-colors shrink-0"
            >
              <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12"><GarageNote /></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <ServiceCard key={s.name} service={s} />
            ))}
          </div>

          <div className="mt-20 bg-[#121212] border border-zinc-800 rounded-xl p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-2">
                Don't see your problem listed?
              </h2>
              <p className="text-zinc-400">Call us — if it can be fixed in a driveway, Mason's can fix it.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <a
                data-testid="services-cta-call-button"
                href={PHONE_TEL}
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-3.5 font-semibold transition-colors"
              >
                <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
              </a>
              <Link
                data-testid="services-cta-request-button"
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-zinc-600 hover:border-white text-white rounded-sm px-6 py-3.5 font-semibold transition-colors"
              >
                Request Service <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
